/**
 * The screens of the wizard (FD §3.2). Each one shows choices as big picture cards and reports changes up;
 * the page around them holds the state and the Back / Skip / Next bar.
 */
import { useRef, type ReactNode } from 'react';
import type { Species, SpeciesPick, Theme, ThemeId } from '../../../shared/types';
import { PlanSketch } from '../shell/PlanSketch';
import type { Layout } from './generate';
import {
  MAX_THEMES,
  SIZES,
  biomeName,
  fitsAbout,
  formatM2,
  LAYOUT_NAME,
  needFor,
  sizeMetres,
  usableLand,
  waterFor,
  WATER_NAME,
  zonesLabel,
  type Fit,
  type LayoutChoice,
  type Picks,
  type SizeId,
  type ThemeAnimals,
  type WaterChoice,
} from './logic';
import { animalEmoji, biomeBackground, LandPicture, PathPicture, themeEmoji, WaterPicture } from './pictures';
import type { Screenshot } from './state';

/** A step's heading and friendly line. */
export function StepHead({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="wz-head">
      <h1>{title}</h1>
      {children && <p className="wz-lead">{children}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// 1 Name

/** Step 1: the zoo's name (optional). */
export function NameStep({ name, placeholder, onChange, onEnter }: { name: string; placeholder: string; onChange(n: string): void; onEnter(): void }) {
  return (
    <>
      <StepHead title="What's your zoo called?">You can leave this empty and we'll call it "{placeholder}". You can rename it any time.</StepHead>
      <div className="wz-name">
        <span className="wz-name-art" aria-hidden="true">🦒🐘🦓</span>
        <input
          autoFocus
          aria-label="Zoo name"
          maxLength={80}
          placeholder={placeholder}
          value={name}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onEnter()}
        />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// 2 Land

interface LandProps {
  start: 'blank' | 'screenshot';
  sizeId: SizeId;
  screenshot: Screenshot | null;
  screenshotError: string;
  preparing: boolean;
  onStart(s: 'blank' | 'screenshot'): void;
  onSize(id: SizeId): void;
  onPickFile(file: File): void;
  onCalibrate(): void;
}

/** Step 2: blank land of a size, or a screenshot of the map. */
export function LandStep(p: LandProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const traced = p.screenshot?.calibration?.boundary ?? null;
  return (
    <>
      <StepHead title="Where do you want to build?">Start on an empty piece of land, or on a screenshot of the map you already have in the game.</StepHead>
      <div className="wz-cards two">
        <PictureCard on={p.start === 'blank'} onClick={() => p.onStart('blank')} title="A blank piece of land" text="We'll make the shape of the zoo for you.">
          <LandPicture scale={0.8} />
        </PictureCard>
        <PictureCard on={p.start === 'screenshot'} onClick={() => p.onStart('screenshot')} title="A screenshot of my map" text="Use a top-down picture of your map in Planet Zoo 2.">
          <span className="wz-emoji-pic" aria-hidden="true">🖼️</span>
        </PictureCard>
      </div>

      <h2 className="wz-sub">{p.start === 'blank' ? 'How big?' : 'How much of the map do you want to plan?'}</h2>
      <div className="wz-cards three">
        {SIZES.map((s, i) => (
          <PictureCard
            key={s.id}
            on={p.sizeId === s.id}
            onClick={() => p.onSize(s.id)}
            title={s.label}
            text={`About ${s.size} m across. Fits about ${fitsAbout(usableLand(s.size))} habitats.`}
          >
            <LandPicture scale={(i + 1) / 3} />
          </PictureCard>
        ))}
      </div>

      {p.start === 'screenshot' && (
        <div className="wz-shot">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) p.onPickFile(f);
            }}
          />
          {p.screenshot ? (
            <div className="wz-shot-row">
              <img src={p.screenshot.url} alt="Your screenshot" />
              <div>
                {p.screenshot.calibration ? (
                  <p>
                    ✅ Lined up{traced ? ' and the zoo edge is marked' : ''}.{' '}
                    {traced
                      ? `That's room for about ${fitsAbout(usableLand(sizeMetres(p.sizeId), traced))} habitats.`
                      : "We'll make a zoo shape on it for you."}
                  </p>
                ) : (
                  <p>Next, show us how big things are on the picture and where the zoo's edge is.</p>
                )}
                <div className="row wrap">
                  <button type="button" className={p.screenshot.calibration ? '' : 'primary'} onClick={p.onCalibrate}>
                    {p.screenshot.calibration ? 'Line it up again' : 'Line it up'}
                  </button>
                  <button type="button" className="quiet" onClick={() => fileRef.current?.click()}>Use another picture</button>
                </div>
              </div>
            </div>
          ) : (
            <button type="button" className="primary big" disabled={p.preparing} onClick={() => fileRef.current?.click()}>
              {p.preparing ? 'Opening the picture…' : '📁 Choose a screenshot…'}
            </button>
          )}
          <p className="hint">Tip: in the game, look straight down from high up and hide the interface before you take the picture.</p>
          {p.screenshotError && <p className="error">{p.screenshotError}</p>}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// 3 Themes

interface ThemesProps {
  themes: Theme[];
  species: Species[];
  chosen: ThemeId[];
  onToggle(id: ThemeId): void;
  onSurprise(): void;
}

/** Step 3: one to five themed areas. */
export function ThemesStep({ themes, species, chosen, onToggle, onSurprise }: ThemesProps) {
  const names = new Map(species.map((s) => [s.id, s.name]));
  const full = chosen.length >= MAX_THEMES;
  return (
    <>
      <StepHead title="Which areas should your zoo have?">
        Pick 1 to {MAX_THEMES}. Visitors walk through them in the order you pick them.
      </StepHead>
      <div className="row wz-surprise">
        <button type="button" onClick={onSurprise}>🎲 Surprise me</button>
        <span className="muted">{chosen.length ? `${chosen.length} picked` : 'Nothing picked yet'}{full ? ` · that's the most you can pick` : ''}</span>
      </div>
      <div className="wz-cards themes">
        {themes.map((t) => {
          const at = chosen.indexOf(t.id);
          const examples = t.suggested.map((id) => names.get(id)).filter((n): n is string => !!n).slice(0, 4);
          return (
            <button
              key={t.id}
              type="button"
              className={`wz-card theme${at >= 0 ? ' on' : ''}`}
              aria-pressed={at >= 0}
              disabled={at < 0 && full}
              onClick={() => onToggle(t.id)}
            >
              <span className="wz-theme-pic" style={{ background: biomeBackground(t) }} aria-hidden="true">
                {themeEmoji(t)}
              </span>
              {at >= 0 && <span className="wz-order" aria-label={`Stop ${at + 1}`}>{at + 1}</span>}
              <strong>{t.name}</strong>
              <span className="wz-chip">{biomeName(t.biome)}</span>
              <span className="small">{t.description}</span>
              <span className="small muted">{examples.join(', ')}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// 4 Animals

interface AnimalsProps {
  groups: ThemeAnimals[];
  picks: Picks;
  onToggle(id: string): void;
  onCount(id: string, count: number): void;
  onAll(group: ThemeAnimals, on: boolean): void;
}

/** Step 4: the suggested animals per theme as tickable cards. */
export function AnimalsStep({ groups, picks, onToggle, onCount, onAll }: AnimalsProps) {
  return (
    <>
      <StepHead title="Which animals?">
        We've ticked a good number for your land. Tap an animal to add or remove it; use − and + to change how many live together.
      </StepHead>
      {groups.map((g) => {
        const all = g.species.every((s) => picks[s.id]?.on);
        return (
          <section key={g.theme.id} className="wz-group">
            <div className="wz-group-head">
              <h2>
                <span aria-hidden="true">{themeEmoji(g.theme)}</span> {g.theme.name}
              </h2>
              {g.species.length > 1 && (
                <button type="button" className="link" onClick={() => onAll(g, !all)}>{all ? 'Untick all' : 'Tick all'}</button>
              )}
            </div>
            {g.species.length === 0 && <p className="muted">No animals for this area yet.</p>}
            <div className="wz-animals">
              {g.species.map((sp) => (
                <AnimalCard key={sp.id} sp={sp} pick={picks[sp.id] ?? { on: false, count: sp.groupSize.typical }} onToggle={() => onToggle(sp.id)} onCount={(n) => onCount(sp.id, n)} />
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}

const KIND_LABEL: Record<Species['kind'], string> = { habitat: '', aviary: 'Aviary', aquarium: 'Aquarium' };

function AnimalCard({ sp, pick, onToggle, onCount }: { sp: Species; pick: { on: boolean; count: number }; onToggle(): void; onCount(n: number): void }) {
  const need = needFor(sp, pick.count);
  const water = waterFor(sp, pick.count);
  const max = Math.max(sp.groupSize.max, sp.groupSize.typical, 1);
  return (
    <div className={`wz-animal${pick.on ? ' on' : ''}`}>
      <button type="button" className="wz-animal-main" aria-pressed={pick.on} onClick={onToggle}>
        <span className="wz-tick" aria-hidden="true">{pick.on ? '✓' : ''}</span>
        <span className="wz-animal-pic" aria-hidden="true">{animalEmoji(sp)}</span>
        <strong>{sp.name}</strong>
        {KIND_LABEL[sp.kind] && <span className="wz-chip">{KIND_LABEL[sp.kind]}</span>}
        <span className="small muted">
          needs about {formatM2(need)} m²{water > 0 ? `, ${formatM2(water)} m² of it water` : ''}
        </span>
      </button>
      {pick.on && (
        <div className="wz-count">
          <button type="button" className="icon-btn" aria-label={`Fewer ${sp.name}`} disabled={pick.count <= 1} onClick={() => onCount(pick.count - 1)}>−</button>
          <span>{pick.count} {pick.count === 1 ? 'animal' : 'animals'}</span>
          <button type="button" className="icon-btn" aria-label={`More ${sp.name}`} disabled={pick.count >= max} onClick={() => onCount(pick.count + 1)}>+</button>
        </div>
      )}
    </div>
  );
}

/** The habitat counter under the animals: "12 habitats — fits nicely" or "may be cramped". */
export function FitCounter({ fit, picks, species }: { fit: Fit; picks: SpeciesPick[]; species: Species[] }) {
  const pct = Math.min(100, Math.round(fit.share * 100));
  return (
    <div className={`wz-fit ${fit.verdict}`} role="status" aria-live="polite">
      <div className="wz-fit-text">
        <strong>{zonesLabel(picks, species)}</strong>
        {fit.verdict === 'nicely' && <span> — fits nicely 👍</span>}
        {fit.verdict === 'cramped' && <span> — too many for this land, may be cramped. You can still go on: animals that don't fit wait in the editor's Unplaced list.</span>}
        {fit.verdict === 'empty' && <span> — tick at least one animal, or go on with an empty zoo.</span>}
      </div>
      <div className="wz-meter" aria-hidden="true">
        <span style={{ width: `${pct}%` }} />
      </div>
      <span className="small muted">
        {formatM2(fit.need)} m² of {formatM2(fit.usable)} m² for animals
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// 5 Style

/** Step 5: the path and water style. */
export function StyleStep({ layout, water, onLayout, onWater }: { layout: LayoutChoice; water: WaterChoice; onLayout(l: LayoutChoice): void; onWater(w: WaterChoice): void }) {
  const paths: [LayoutChoice, string, string][] = [
    ['loop', 'One big loop', 'An easy walk past everything.'],
    ['cross', 'Loop with a shortcut', 'A path across the middle too.'],
    ['eight', 'Figure-eight', 'Two loops that meet at the entrance.'],
    ['surprise', 'Surprise me', 'Each layout tries something different.'],
  ];
  const waters: [WaterChoice, string][] = [
    ['lake', 'A lake'],
    ['river', 'A river'],
    ['both', 'Lake and river'],
    ['none', 'No water'],
    ['surprise', 'Surprise me'],
  ];
  return (
    <>
      <StepHead title="What should it look like?">Pick a path style and some water. You can change everything later in the editor.</StepHead>
      <h2 className="wz-sub">Paths</h2>
      <div className="wz-cards four">
        {paths.map(([id, title, text]) => (
          <PictureCard key={id} on={layout === id} onClick={() => onLayout(id)} title={title} text={text}>
            <PathPicture style={id} />
          </PictureCard>
        ))}
      </div>
      <h2 className="wz-sub">Water</h2>
      <div className="wz-cards five">
        {waters.map(([id, title]) => (
          <PictureCard key={id} on={water === id} onClick={() => onWater(id)} title={title}>
            <WaterPicture style={id} />
          </PictureCard>
        ))}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// 6 Layout

interface LayoutProps {
  layouts: Layout[] | null;
  busy: boolean;
  error: string;
  picked: number;
  wanted: number;
  onPick(i: number): void;
  onOpen(i: number): void;
  onOthers(): void;
  onRetry(): void;
}

/** Step 6: three generated layouts side by side. */
export function LayoutStep(p: LayoutProps) {
  return (
    <>
      <StepHead title="Pick the layout you like best">
        Here are three ideas made from your choices. Pick one to open it in the editor, where you can move everything around.
      </StepHead>
      <div className="row wz-surprise">
        <button type="button" onClick={p.onOthers} disabled={p.busy}>🔄 Show me 3 others</button>
      </div>
      {p.error ? (
        <div className="banner warn">
          <span aria-hidden="true">⚠️</span>
          <span>Something went wrong making the layouts. {p.error}</span>
          <button type="button" onClick={p.onRetry}>Try again</button>
        </div>
      ) : (
        <div className="wz-layouts">
          {(p.layouts ?? [null, null, null]).map((l, i) => (
            <LayoutCard key={i} index={i} layout={p.busy ? null : l} on={p.picked === i} wanted={p.wanted} onPick={() => p.onPick(i)} onOpen={() => p.onOpen(i)} />
          ))}
        </div>
      )}
    </>
  );
}

function LayoutCard({ index, layout, on, wanted, onPick, onOpen }: { index: number; layout: Layout | null; on: boolean; wanted: number; onPick(): void; onOpen(): void }) {
  if (!layout) {
    return (
      <div className="wz-layout loading" aria-busy="true">
        <div className="wz-layout-pic" />
        <span className="muted">Drawing layout {index + 1}…</span>
      </div>
    );
  }
  const { doc, unplaced } = layout;
  const style = describe(doc);
  return (
    <button type="button" className={`wz-layout${on ? ' on' : ''}`} aria-pressed={on} onClick={onPick} onDoubleClick={onOpen}>
      <div className="wz-layout-pic">
        <PlanSketch doc={doc} />
      </div>
      <strong>Layout {index + 1}</strong>
      <span className="small">{style}</span>
      <span className={`small ${unplaced.length ? 'wz-warn-text' : 'muted'}`}>
        {unplaced.length
          ? `${wanted - unplaced.length} of ${wanted} fit on the map; ${unplaced.length} will wait in Unplaced`
          : wanted
            ? `All ${wanted} fit on the map`
            : 'Empty zoo, ready for animals'}
      </span>
    </button>
  );
}

/** "One big loop · Lake" from what's in a generated doc. */
function describe(doc: Layout['doc']): string {
  const roads = Object.values(doc.roads);
  const loops = roads.filter((r) => r.kind === 'main').length;
  const cross = roads.some((r) => r.kind === 'cross');
  const path = loops >= 2 ? LAYOUT_NAME.eight : cross ? LAYOUT_NAME.cross : LAYOUT_NAME.loop;
  const lake = Object.values(doc.areas).some((a) => a.kind === 'lake');
  const river = Object.keys(doc.rivers).length > 0;
  const water = lake && river ? WATER_NAME.both : lake ? WATER_NAME.lake : river ? WATER_NAME.river : WATER_NAME.none;
  return `${path} · ${water}`;
}

// ---------------------------------------------------------------------------------------------

/** A big picture card that can be on or off. */
export function PictureCard({ on, onClick, title, text, children }: { on: boolean; onClick(): void; title: string; text?: string; children: ReactNode }) {
  return (
    <button type="button" className={`wz-card${on ? ' on' : ''}`} aria-pressed={on} onClick={onClick}>
      <span className="wz-pic">{children}</span>
      <strong>{title}</strong>
      {text && <span className="small muted">{text}</span>}
    </button>
  );
}
